import React, { useEffect, useRef, useState } from "react";
import {
	Box,
	Dialog,
	IconButton,
	Tooltip,
	useMediaQuery
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { Close, GetApp, RotateLeft } from "@material-ui/icons";

import api from "../../services/api";

const useStyles = makeStyles(theme => ({
	thumbnailButton: {
		appearance: "none",
		background: "transparent",
		border: 0,
		borderRadius: 8,
		cursor: "zoom-in",
		display: "block",
		margin: 0,
		maxWidth: "100%",
		padding: 0,
		WebkitTapHighlightColor: "transparent",
		"&:focus-visible": {
			outline: `3px solid ${theme.palette.primary.main}`,
			outlineOffset: 2
		}
	},
	messageMedia: {
		borderRadius: 8,
		display: "block",
		height: "auto",
		maxWidth: "100%",
		objectFit: "cover",
		width: 250
	},
	dialogPaper: {
		background: "rgba(7, 12, 18, 0.96)",
		margin: 12,
		maxHeight: "calc(100dvh - 24px)",
		maxWidth: "calc(100vw - 24px)",
		overflow: "hidden",
		width: "calc(100vw - 24px)"
	},
	viewer: {
		alignItems: "center",
		display: "flex",
		height: "100%",
		justifyContent: "center",
		minHeight: 240,
		overflow: "auto",
		padding: "64px 20px 24px",
		touchAction: "pan-x pan-y",
		width: "100%"
	},
	fullImage: {
		display: "block",
		maxHeight: "calc(100dvh - 100px)",
		maxWidth: "100%",
		objectFit: "contain",
		transformOrigin: "center",
		transition: "transform 160ms ease"
	},
	toolbar: {
		display: "flex",
		gap: 6,
		position: "fixed",
		right: "max(12px, env(safe-area-inset-right))",
		top: "max(12px, env(safe-area-inset-top))",
		zIndex: theme.zIndex.modal + 2
	},
	toolbarButton: {
		background: "rgba(0, 0, 0, 0.7)",
		color: "#fff",
		minHeight: 44,
		minWidth: 44,
		"&:hover": { background: "rgba(0, 0, 0, 0.86)" }
	}
}));

const ModalImageCors = ({ imageUrl }) => {
	const classes = useStyles();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const [fetching, setFetching] = useState(true);
	const [blobUrl, setBlobUrl] = useState("");
	const [fetchFailed, setFetchFailed] = useState(false);
	const [open, setOpen] = useState(false);
	const [rotation, setRotation] = useState(0);
	const ownsHistoryEntry = useRef(false);

	useEffect(() => {
		let active = true;
		let generatedUrl = "";
		if (!imageUrl) {
			setFetching(false);
			setBlobUrl("");
			return undefined;
		}

		setFetching(true);
		setFetchFailed(false);
		setBlobUrl("");

		const fetchImage = async () => {
			try {
				let normalizedUrl = imageUrl;
				if (normalizedUrl.startsWith("http://") || normalizedUrl.startsWith("https://")) {
					try {
						normalizedUrl = new URL(normalizedUrl).pathname;
					} catch (error) {
						console.warn("[ModalImageCors] Erro ao parsear URL:", error);
					}
				} else if (!normalizedUrl.startsWith("/")) {
					normalizedUrl = `/${normalizedUrl}`;
				}

				const { data, headers } = await api.get(normalizedUrl, { responseType: "blob" });
				generatedUrl = window.URL.createObjectURL(
					new Blob([data], { type: headers["content-type"] })
				);
				if (active) setBlobUrl(generatedUrl);
			} catch (error) {
				console.warn("[ModalImageCors] Falha ao buscar via api, usando URL direta:", imageUrl);
				if (active) setFetchFailed(true);
			} finally {
				if (active) setFetching(false);
			}
		};

		fetchImage();
		return () => {
			active = false;
			if (generatedUrl) window.URL.revokeObjectURL(generatedUrl);
		};
	}, [imageUrl]);

	useEffect(() => {
		if (!open) return undefined;
		setRotation(0);
		const handleBack = () => {
			ownsHistoryEntry.current = false;
			setOpen(false);
		};
		window.history.pushState({ ...(window.history.state || {}), estratizaImageModal: true }, "");
		ownsHistoryEntry.current = true;
		window.addEventListener("popstate", handleBack);
		return () => window.removeEventListener("popstate", handleBack);
	}, [open]);

	const displayUrl = fetching ? imageUrl : (blobUrl || (fetchFailed ? imageUrl : ""));

	const closeViewer = () => {
		if (ownsHistoryEntry.current && window.history.state?.estratizaImageModal) {
			window.history.back();
			return;
		}
		ownsHistoryEntry.current = false;
		setOpen(false);
	};

	const downloadImage = () => {
		const downloadUrl = blobUrl || displayUrl || imageUrl;
		if (!downloadUrl) return;

		let fileName = "imagem";
		try {
			const imagePath = new URL(imageUrl, window.location.origin).pathname;
			fileName = decodeURIComponent(imagePath.split("/").pop()) || fileName;
		} catch (error) {
			console.warn("[ModalImageCors] Nao foi possivel identificar o nome da imagem:", error);
		}

		const link = document.createElement("a");
		link.href = downloadUrl;
		link.download = fileName;
		link.rel = "noopener";
		document.body.appendChild(link);
		link.click();
		link.remove();
	};

	return (
		<>
			<button
				type="button"
				className={classes.thumbnailButton}
				onClick={() => displayUrl && setOpen(true)}
				aria-label="Ampliar imagem"
			>
				<img className={classes.messageMedia} src={displayUrl || imageUrl} alt="Imagem da conversa" />
			</button>
			<Dialog
				open={open}
				onClose={closeViewer}
				fullScreen={isMobile}
				maxWidth={false}
				PaperProps={{ className: classes.dialogPaper }}
				aria-label="Imagem ampliada"
			>
				<Box className={classes.toolbar}>
					<Tooltip title="Baixar imagem">
						<IconButton className={classes.toolbarButton} onClick={downloadImage} aria-label="Baixar imagem">
							<GetApp />
						</IconButton>
					</Tooltip>
					<Tooltip title="Girar imagem">
						<IconButton className={classes.toolbarButton} onClick={() => setRotation(value => (value - 90) % 360)} aria-label="Girar imagem">
							<RotateLeft />
						</IconButton>
					</Tooltip>
					<Tooltip title="Fechar imagem">
						<IconButton className={classes.toolbarButton} onClick={closeViewer} aria-label="Fechar imagem">
							<Close />
						</IconButton>
					</Tooltip>
				</Box>
				<Box className={classes.viewer} onClick={event => event.target === event.currentTarget && closeViewer()}>
					<img
						className={classes.fullImage}
						src={displayUrl || imageUrl}
						alt="Imagem ampliada da conversa"
						style={{ transform: `rotate(${rotation}deg)` }}
					/>
				</Box>
			</Dialog>
		</>
	);
};

export default ModalImageCors;
